// @ts-check

import { onBeforeUnmount, onMounted } from "vue";
import { useHostElement } from "#ce/composables/useHostElement";
import { holdMountReady } from "#ce/framework/mount-ready";
import { reportClientError } from "#ce/utils/error-report";

/**
 * @typedef {() => void} LegacyTeardown
 */

/**
 * @template LegacyModule
 * @typedef {object} LegacyHostOptions
 * @property {() => Promise<LegacyModule>} load - Imports the app's `legacy/` adapter. Called once,
 *   when the host mounts, so the legacy code loads only on a page that carries the tag. The adapter
 *   imports no module a legacy entry also imports: the build splits a shared module into its own
 *   chunk, and that chunk then runs before the code the entry imports ahead of it.
 * @property {(legacy: LegacyModule, context: { host: HTMLElement }) => LegacyTeardown | Promise<LegacyTeardown>} boot -
 *   Runs the legacy boot with the arguments the ERB passed before the tag, rendering into the
 *   host's light DOM. Returns the function that undoes it.
 * @property {boolean} [afterLegacyBundle=true] - Waits for `Sparkle.ready` before importing, the
 *   moment a `js_content` boot runs. The page's legacy bundle has then evaluated every module it
 *   shares with the adapter. Pass `false` only for an adapter that imports every global it reads.
 */

/**
 * @param {unknown} value
 * @returns {value is object}
 */
function isObjectOrFunction(value) {
  return (typeof value === "object" && value !== null) || typeof value === "function";
}

/**
 * @returns {unknown}
 */
function legacyReadyQueue() {
  const sparkle = "Sparkle" in window ? window.Sparkle : undefined;

  if (!isObjectOrFunction(sparkle)) {
    return undefined;
  }

  return "ready" in sparkle ? sparkle.ready : undefined;
}

/**
 * @param {unknown} error
 * @returns {string}
 */
function describeError(error) {
  const stack = isObjectOrFunction(error) && "stack" in error ? error.stack : undefined;

  return String(stack ?? error);
}

/**
 * Resolves once the page's legacy bundle has flushed `Sparkle.ready`, or at once on a page
 * without the ready queue.
 *
 * @returns {Promise<void>}
 */
function whenLegacyBundleReady() {
  const ready = legacyReadyQueue();

  if (typeof ready !== "function") {
    return Promise.resolve();
  }

  return new Promise((resolve) => ready(resolve));
}

/**
 * Mounts a legacy app inside an sp-app host and returns `booted`, which settles once the legacy
 * boot has finished. The import starts when the host mounts, after `Sparkle.ready` by default.
 *
 * The host is mount-ready only after the boot resolves, so `whenMountReady` waits for the legacy
 * app. A failed import or boot is reported to Honeybadger, marks the host `data-sp-ce-failed`, and
 * rejects `booted`. Unmounting runs the teardown the boot returned, also when the boot finishes
 * after the host is gone.
 *
 * @template LegacyModule
 * @param {LegacyHostOptions<LegacyModule>} options
 * @returns {{ booted: Promise<void> }}
 */
export function useLegacyHost({ load, boot, afterLegacyBundle = true }) {
  const host = /** @type {HTMLElement} */ (useHostElement());
  let mounted = false;
  /** @type {LegacyTeardown | undefined} */
  let teardown;
  /** @type {() => void} */
  let resolveBooted = () => {};
  /** @type {(reason: unknown) => void} */
  let rejectBooted = () => {};

  /** @type {Promise<void>} */
  const booted = new Promise((resolve, reject) => {
    resolveBooted = resolve;
    rejectBooted = reject;
  });

  holdMountReady(host, booted);

  async function bootLegacy() {
    mounted = true;

    try {
      if (afterLegacyBundle) {
        await whenLegacyBundleReady();
      }

      if (!mounted) {
        resolveBooted();
        return;
      }

      const legacy = await load();

      if (!mounted) {
        resolveBooted();
        return;
      }

      const undo = await boot(legacy, { host });

      if (mounted) {
        teardown = undo;
      } else {
        undo?.();
      }

      resolveBooted();
    } catch (error) {
      reportClientError(`<${host.localName}> legacy boot failed`, {
        description: describeError(error),
        // On a host CMS page the report must reach the Sparkle origin this module loads from.
        baseUrl: import.meta.url,
        omitPage: true,
      });
      rejectBooted(error);
    }
  }

  function tearDownLegacy() {
    mounted = false;
    teardown?.();
    teardown = undefined;
  }

  onMounted(bootLegacy);
  onBeforeUnmount(tearDownLegacy);

  return { booted };
}
